import { css, html, LitElement, nothing, type PropertyValues } from "lit";

import { formatSize } from "../lib/format";
import { controlStyles } from "../styles/tokens";

/**
 * <modulina-upload-panel> — the W3 upload affordance (spec-web-ui §2
 * "Upload"), rendered by the browser component ONLY when the transport's
 * `caps.upload` is true AND a project is selected. Pure props-in/events-out
 * like every other child: it never sees the transport — the parent owns the
 * upload flow (pick → fresh name-match check → confirm → send → refresh).
 *
 * States (all parent-driven except the local message draft):
 *   idle       — ONE of two source-mode variants (spec-upload-unification
 *                §3 — capability DATA via `sourceMode`, never host if's):
 *                  "file"        — the "Įkelti failą…" file-picker button
 *                                  (native input, triggered synchronously
 *                                  from the click so the browser's
 *                                  user-activation rules are honored)
 *                  "activeModel" — the "Įkelti aktyvų modelį" button (no
 *                                  file input at all; emits `model-pick`,
 *                                  the parent asks the transport for the
 *                                  active model's facts)
 *                BOTH variants name an armed `returnTarget` instead of the
 *                plain upload wording — a return must never look like an
 *                ordinary upload in either shell.
 *   chosen     — file name + size (+ the optional muted `pendingNote` —
 *                the dirty-model note), optional message field, the append
 *                confirm line when the name matches an existing artifact
 *                ("Bus sukurta nauja versija …" — append-only must never
 *                surprise), "Įkelti"/"Tęsti" + "Atšaukti"
 *   uploading  — <progress> + percent + "Atšaukti" (abort). A native
 *                <progress> element on purpose: CSP default-src 'self'
 *                blocks inline style attributes, so no width-styled div;
 *                no `value` attribute = indeterminate (fetch path sends
 *                no progress events).
 *
 * chosen + uploading are SHARED verbatim between the source modes — only
 * the idle step differs (the canonical-UX-1:1 owner decision).
 *
 * Events out: `file-chosen` {file}, `model-pick` (activeModel idle),
 * `upload-submit` {message}, `upload-cancel` (pre-send reset),
 * `upload-abort` (mid-send).
 */

export interface UploadAppendInfo {
  artifactName: string;
  nextVersionNumber: number;
}

export interface UploadProgress {
  sentBytes: number;
  totalBytes: number;
}

export class ModulinaUploadPanel extends LitElement {
  static override properties = {
    sourceMode: { attribute: false },
    returnTarget: { attribute: false },
    fileName: { attribute: false },
    fileSize: { attribute: false },
    pendingNote: { attribute: false },
    appendInfo: { attribute: false },
    uploading: { attribute: false },
    progress: { attribute: false },
    message: { state: true },
  };

  /** Which idle variant to render (capability data from the parent —
   * spec-upload-unification §3): "file" = native file picker (browser),
   * "activeModel" = the active-model button (SketchUp). Pending/confirm/
   * progress states are IDENTICAL in both modes. */
  declare sourceMode: "file" | "activeModel";
  /** The file being RETURNED (spec-artifact-locking §8), when the flow was
   * started from a taken row's „Grąžinti" — the idle step then names it
   * instead of the generic upload button, in BOTH source modes ("file":
   * which file to pick; "activeModel": where the model is going back to).
   * null = an ordinary upload. */
  declare returnTarget: string | null;
  /** Chosen file's name/size (parent state; null = idle). */
  declare fileName: string | null;
  declare fileSize: number | null;
  /** Optional muted note under the pending file line (the dirty-model
   * note — parent-worded; null = no note). */
  declare pendingNote: string | null;
  /** Non-null = the name matches ONE existing artifact → confirm-append UI. */
  declare appendInfo: UploadAppendInfo | null;
  declare uploading: boolean;
  declare progress: UploadProgress | null;
  /** Local draft of the optional version message. */
  declare message: string;

  constructor() {
    super();
    this.sourceMode = "file";
    this.returnTarget = null;
    this.fileName = null;
    this.fileSize = null;
    this.pendingNote = null;
    this.appendInfo = null;
    this.uploading = false;
    this.progress = null;
    this.message = "";
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: block;
      }
      .panel {
        border: 1px solid var(--m-color-border);
        border-radius: var(--m-radius);
        background: var(--m-color-surface);
        box-shadow: var(--m-shadow);
        padding: var(--m-space-3);
        display: flex;
        flex-direction: column;
        gap: var(--m-space-2);
      }
      input[type="file"] {
        display: none;
      }
      .file-line {
        font-size: var(--m-fs-small);
      }
      .file-line .size {
        color: var(--m-color-text-muted);
      }
      .pending-note {
        margin: 0;
        font-size: var(--m-fs-small);
        color: var(--m-color-text-muted);
      }
      .confirm {
        font-size: var(--m-fs-small);
        background: var(--m-color-accent-soft);
        border-radius: var(--m-radius-small);
        padding: var(--m-space-2) var(--m-space-3);
      }
      .buttons {
        display: flex;
        gap: var(--m-space-2);
      }
      .progress-row {
        display: flex;
        align-items: center;
        gap: var(--m-space-3);
      }
      progress {
        flex: 1;
        height: 10px;
        accent-color: var(--m-color-accent);
      }
      .pct {
        font-size: var(--m-fs-small);
        color: var(--m-color-text-muted);
        min-width: 3.5ch;
        text-align: right;
      }
    `,
  ];

  protected override willUpdate(changed: PropertyValues<this>): void {
    // Parent reset (successful upload / cancel): drop the message draft
    // with the file, so the next upload starts clean.
    if (changed.has("fileName") && this.fileName === null) {
      this.message = "";
    }
  }

  override render() {
    if (this.fileName === null) {
      // The ONLY step the source mode changes (spec-upload-unification §2):
      // "activeModel" has no file input — the model IS the source, the
      // parent fetches its facts on `model-pick`.
      if (this.sourceMode === "activeModel") {
        // An armed return must be VISIBLE here too (the file branch has said
        // `Grąžinti „X"…` since the locking arc): otherwise the user presses
        // „Grąžinti" on a row and the panel still offers a plain upload, with
        // no sign of which file the model is going back to (bug report 127d).
        return html`
          <div>
            <button class="primary" @click=${this.emitModelPick}>
              ${this.returnTarget === null ? "Įkelti aktyvų modelį" : "Grąžinti aktyvų modelį"}
            </button>
            ${this.returnTarget === null
              ? nothing
              : html`<p class="pending-note">
                  Aktyvus modelis bus grąžintas į failą „${this.returnTarget}".
                </p>`}
          </div>
        `;
      }
      return html`
        <div>
          <button class="primary" @click=${this.openPicker}>
            ${this.returnTarget === null
              ? "Įkelti failą…"
              : `Grąžinti „${this.returnTarget}"…`}
          </button>
          ${this.returnTarget === null
            ? nothing
            : html`<p class="pending-note">
                Pasirinkite pataisytą failą „${this.returnTarget}".
              </p>`}
          <input
            type="file"
            aria-hidden="true"
            tabindex="-1"
            @change=${this.onFileChange}
          />
        </div>
      `;
    }
    if (this.uploading) {
      const p = this.progress;
      const pct =
        p && p.totalBytes > 0
          ? Math.min(100, Math.floor((p.sentBytes / p.totalBytes) * 100))
          : null;
      return html`
        <div class="panel">
          <span class="file-line">Įkeliama: ${this.fileName}</span>
          <div class="progress-row">
            ${pct === null
              ? html`<progress aria-label="Įkėlimo eiga"></progress>
                  <span class="pct" aria-live="polite">…</span>`
              : html`<progress
                    max="100"
                    value=${pct}
                    aria-label="Įkėlimo eiga"
                  ></progress>
                  <span class="pct" aria-live="polite">${pct}%</span>`}
            <button @click=${this.emitAbort}>Atšaukti</button>
          </div>
        </div>
      `;
    }
    return html`
      <div class="panel">
        <span class="file-line">
          Failas: ${this.fileName}
          ${this.fileSize === null
            ? nothing
            : html`<span class="size">(${formatSize(this.fileSize)})</span>`}
        </span>
        ${this.pendingNote
          ? html`<p class="pending-note">${this.pendingNote}</p>`
          : nothing}
        <div>
          <label for="upload-message">Žinutė (nebūtina)</label>
          <input
            id="upload-message"
            type="text"
            maxlength="2000"
            .value=${this.message}
            @input=${(e: Event) => (this.message = (e.target as HTMLInputElement).value)}
            @keydown=${this.onMessageKeydown}
          />
        </div>
        ${this.appendInfo
          ? html`<p class="confirm" role="status">
              Bus sukurta nauja versija: „${this.appendInfo.artifactName}"
              (v${this.appendInfo.nextVersionNumber}). Tęsti?
            </p>`
          : nothing}
        <div class="buttons">
          <button class="primary" @click=${this.emitSubmit}>
            ${this.appendInfo ? "Tęsti" : "Įkelti"}
          </button>
          <button @click=${this.emitCancel}>Atšaukti</button>
        </div>
      </div>
    `;
  }

  protected override updated(changed: PropertyValues<this>): void {
    // Keyboard flow: after a file lands, continue at the message field;
    // after reset, back to the picker button (parent renders us
    // continuously, so focus stays inside the panel on both transitions).
    if (changed.has("fileName") && changed.get("fileName") !== undefined) {
      const target = this.renderRoot.querySelector<HTMLElement>(
        this.fileName === null ? "button.primary" : "#upload-message",
      );
      target?.focus();
    }
  }

  /**
   * Open the native file picker. PUBLIC because the return flow
   * (spec-artifact-locking §8: „Grąžinti" = upload) starts at a table row,
   * not at this panel: the parent calls this SYNCHRONOUSLY from the row's
   * click handler, so the browser still sees the user gesture that a file
   * input requires. Kept an arrow so the template binding keeps `this`.
   */
  openPicker = (): void => {
    this.renderRoot.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  };

  /** "activeModel" idle: the parent owns the transport, so picking the
   * active model is an intent UP — the parent calls getModelInfo and
   * drives the shared pending state from its answer. */
  private emitModelPick = (): void => {
    this.dispatchEvent(new CustomEvent("model-pick", { bubbles: true, composed: true }));
  };

  private onFileChange = (e: Event): void => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset so re-picking the SAME file after a cancel fires change again.
    input.value = "";
    if (!file) return;
    this.dispatchEvent(
      new CustomEvent("file-chosen", { detail: { file }, bubbles: true, composed: true }),
    );
  };

  private onMessageKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      this.emitSubmit();
    }
  };

  private emitSubmit = (): void => {
    this.dispatchEvent(
      new CustomEvent("upload-submit", {
        detail: { message: this.message },
        bubbles: true,
        composed: true,
      }),
    );
  };

  private emitCancel = (): void => {
    this.dispatchEvent(new CustomEvent("upload-cancel", { bubbles: true, composed: true }));
  };

  private emitAbort = (): void => {
    this.dispatchEvent(new CustomEvent("upload-abort", { bubbles: true, composed: true }));
  };
}

customElements.define("modulina-upload-panel", ModulinaUploadPanel);
